import React, { useEffect, useRef, useState } from "react";
import { ExpandOutlined, LeftOutlined, RightOutlined } from "@ant-design/icons";

const THUMB_SCROLL_STEP = 200;

function ThumbButton({
  src,
  active,
  lazy,
  scrollerRef,
  onError,
  onSelect,
  label,
}) {
  const buttonRef = useRef(null);
  const [ready, setReady] = useState(!lazy || active);

  useEffect(() => {
    if (active) {
      setReady(true);
      return undefined;
    }
    if (!lazy || ready) return undefined;
    const node = buttonRef.current;
    if (!node || typeof IntersectionObserver === "undefined") {
      setReady(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setReady(true);
          observer.disconnect();
        }
      },
      { root: scrollerRef?.current || null, rootMargin: "160px" }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [active, lazy, ready, scrollerRef]);

  return (
    <button
      ref={buttonRef}
      type="button"
      role="listitem"
      className={active ? "is-active" : ""}
      onClick={onSelect}
      aria-label={label}
      aria-current={active ? "true" : undefined}
    >
      {ready ? (
        <img
          src={src}
          alt=""
          decoding="async"
          loading={lazy ? "lazy" : undefined}
          onError={onError}
        />
      ) : null}
    </button>
  );
}

/**
 * Package-profile hotel gallery SSOT (mock + hpmShell live).
 * LTR chrome (even inside RTL page): stage + side arrows + "current / total"
 * counter + expand + thumbs (first/active on the left) + right scroll chevron.
 */
export default function HpmHeroGallery({
  images = [],
  alt = "",
  lazyImages = false,
}) {
  const incoming = Array.isArray(images) ? images.filter(Boolean) : [];
  const incomingKey = incoming.join("|");
  const [index, setIndex] = useState(0);
  const [lightbox, setLightbox] = useState(false);
  const [broken, setBroken] = useState(() => new Set());
  const thumbsRef = useRef(null);
  const gallery = lazyImages
    ? incoming.filter((src) => !broken.has(src))
    : incoming;

  useEffect(() => {
    setIndex(0);
    setBroken(new Set());
  }, [incomingKey]);

  const hideBroken = (src) => {
    if (!lazyImages || !src) return;
    setBroken((prev) => {
      if (prev.has(src)) return prev;
      const next = new Set(prev);
      next.add(src);
      return next;
    });
  };

  useEffect(() => {
    if (!lightbox) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        setLightbox(false);
      } else if (e.key === "ArrowLeft" && gallery.length > 1) {
        e.preventDefault();
        setIndex((i) => (i - 1 + gallery.length) % gallery.length);
      } else if (e.key === "ArrowRight" && gallery.length > 1) {
        e.preventDefault();
        setIndex((i) => (i + 1) % gallery.length);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox, gallery.length]);

  const safeIndex =
    gallery.length > 0 ? Math.min(index, gallery.length - 1) : 0;

  useEffect(() => {
    const el = thumbsRef.current;
    if (!el || !gallery.length) return;
    const active = el.querySelector("button.is-active");
    active?.scrollIntoView({
      behavior: "smooth",
      inline: "nearest",
      block: "nearest",
    });
  }, [safeIndex, gallery.length]);

  if (!gallery.length) return null;
  const mainImage = gallery[safeIndex];
  const multi = gallery.length > 1;

  const go = (delta) => {
    if (!multi) return;
    setIndex((i) => (i + delta + gallery.length) % gallery.length);
  };

  // Thumbs strip is forced LTR (mock SSOT): first thumb on the left, scroll → right.
  const scrollThumbs = (delta) => {
    const el = thumbsRef.current;
    if (!el) return;
    el.scrollBy({ left: delta, behavior: "smooth" });
  };

  const counterLabel = `${safeIndex + 1} / ${gallery.length}`;

  return (
    <>
      <div className="hpm-hero__gallery" dir="ltr">
        <div className="hpm-hero__stage">
          <img
            src={mainImage}
            alt={alt || "מלון"}
            onError={lazyImages ? () => hideBroken(mainImage) : undefined}
          />
          {multi ? (
            <>
              <button
                type="button"
                className="hpm-hero__nav hpm-hero__nav--prev"
                onClick={() => go(-1)}
                aria-label="תמונה קודמת"
              >
                <LeftOutlined className="hpm-hero__nav-ico" />
              </button>
              <button
                type="button"
                className="hpm-hero__nav hpm-hero__nav--next"
                onClick={() => go(1)}
                aria-label="תמונה הבאה"
              >
                <RightOutlined className="hpm-hero__nav-ico" />
              </button>
            </>
          ) : null}
          {/* dir=ltr: prevent RTL bidi from rendering "1 / 6" as "6/1" */}
          <span
            className="hpm-hero__counter"
            aria-live="polite"
            dir="ltr"
          >
            {counterLabel}
          </span>
          <button
            type="button"
            className="hpm-hero__expand"
            onClick={() => setLightbox(true)}
            aria-label="פתיחת גלריה במסך מלא"
          >
            <ExpandOutlined />
          </button>
        </div>

        {multi ? (
          <div className="hpm-hero__thumbs-row">
            <div
              className="hpm-hero__thumbs"
              ref={thumbsRef}
              role="list"
              aria-label="בחירת תמונה"
            >
              {gallery.map((src, i) => (
                <ThumbButton
                  key={`${src}-${i}`}
                  src={src}
                  active={i === safeIndex}
                  lazy={lazyImages}
                  scrollerRef={thumbsRef}
                  onSelect={() => setIndex(i)}
                  onError={lazyImages ? () => hideBroken(src) : undefined}
                  label={`תמונה ${i + 1}`}
                />
              ))}
            </div>
            <button
              type="button"
              className="hpm-hero__thumbs-nav"
              onClick={() => scrollThumbs(THUMB_SCROLL_STEP)}
              aria-label="גלול ממוזערות"
            >
              <RightOutlined className="hpm-hero__nav-ico" />
            </button>
          </div>
        ) : null}
      </div>

      {lightbox ? (
        <div
          className="hpm-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label="גלריה"
        >
          <button
            type="button"
            className="hpm-lightbox__close"
            onClick={() => setLightbox(false)}
          >
            סגור
          </button>
          <img
            src={mainImage}
            alt={alt || "מלון"}
            onError={lazyImages ? () => hideBroken(mainImage) : undefined}
          />
          {multi ? (
            <div className="hpm-lightbox__nav">
              <button type="button" onClick={() => go(-1)}>
                הקודם
              </button>
              <button type="button" onClick={() => go(1)}>
                הבא
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
    </>
  );
}
